<script setup lang="ts">
import { useMediaQuery, useWindowScroll } from '@vueuse/core'
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'

import { ASSISTANT_CANVAS_TARGET_ID, useAssistantStore } from '@/app/stores/assistant'
import { useUiStore } from '@/app/stores/ui'
import { CONTENT_SIDEBAR_TARGET_ID } from '@/shared/ui'

import AppBreadcrumb from './components/AppBreadcrumb.vue'
import AppNavbar from './components/AppNavbar.vue'
import VerticalNav from './components/VerticalNav.vue'
import { useSessionChrome } from './useSessionChrome'

/**
 * The application frame (wp/shell §4.15): vertical nav (an overlay below 1200 px that any route change closes),
 * floating navbar, content, scroll-to-top. Content renderer by `meta.contentRenderer`: the default one shows the
 * breadcrumb and the page; `sidebar-left` (the inbox) shows the breadcrumb and one bordered frame holding a `<Teleport>`
 * target `#content-renderer-sidebar-left` for the page's sidebar (legacy portal-vue; the page renders it with the kit's
 * `AppContentSidebar`, which is a column from 992 px and slides in over the page below) and the page. The frame fills
 * the viewport height as legacy's `content-area-wrapper`, so the page's lists scroll inside it. The suggestion box
 * legacy showed under every page was dropped (owner, 2026-10-25), and so was the "© <year> Skileo" footer (2026-10-29;
 * the line is not shown anywhere for now): the page ends one gutter above the window's edge, as the nav does.
 *
 * The assistant (src/modules/assistant, a global widget) docks as a third column on the right from 1200 px: the main
 * column keeps clear of it by the panel's width (`--assistant-width`), as it keeps clear of the nav on the left.
 * While a surface's canvas is open (the AI mode's, `assistant.canvasOpen` — docs/ai-mode) it stands in for the page:
 * the nav, the breadcrumb and the page (kept mounted, so it is as the user left it) hide, and the content
 * area is the canvas's teleport target, as tall as the window leaves under the navbar.
 *
 * On mount it loads what the legacy navbar and nav loaded (`useSessionChrome`, shared with the AI layout): the Staff
 * permission refresh, the workspaces, the account (language, date format) and the badge polling, which stops when the
 * layout unmounts or the session ends (NEW-14).
 */
const route = useRoute()
const assistant = useAssistantStore()
const ui = useUiStore()
const xl = useMediaQuery('(min-width: 1200px)')
const lg = useMediaQuery('(min-width: 992px)')
const { y: scrollY } = useWindowScroll()
/** The navbar's fade band shows once the page has scrolled under it; at the top the bar sits on the clean canvas. */
const scrolled = computed(() => scrollY.value > 4)

const sidebarLeft = computed(() => route.meta.contentRenderer === 'sidebar-left')
const collapsed = computed(() => xl.value && ui.navCollapsed)
const assistantDocked = computed(() => xl.value && assistant.open)
/** The pill floats over the bottom of the page: the frame ends above it, so the last rows stay reachable. */
const assistantPill = computed(
  () => assistant.mounted && !assistant.open && !assistant.pillHidden && !route.meta.onboarding,
)
const canvas = computed(() => assistant.canvasOpen && !route.meta.onboarding)

watch(
  () => route.fullPath,
  () => ui.setNavOpen(false),
)

useSessionChrome()
</script>

<template>
  <div
    class="layout-vertical"
    :class="{
      'is-collapsed': collapsed,
      'is-xl': xl,
      'has-assistant': assistantDocked,
      'has-assistant-pill': assistantPill,
      'has-canvas': canvas,
    }"
    :style="{ '--assistant-width': assistant.panelWidth }"
    data-layout="vertical"
  >
    <!-- A wrapper: the nav has two roots (the panel and its edge handle), so v-show on it would not land. -->
    <Transition name="layout-fade">
      <div v-show="!canvas" class="layout-vertical__nav">
        <VerticalNav :xl="xl" :open="ui.navOpen" @close="ui.setNavOpen(false)" />
      </div>
    </Transition>
    <div
      v-if="!xl && ui.navOpen && !canvas"
      class="layout-vertical__overlay"
      data-testid="nav-overlay"
      @click="ui.setNavOpen(false)"
    />
    <div class="layout-vertical__main">
      <div class="layout-vertical__navbar">
        <div class="edge-fade" :class="{ 'is-shown': scrolled }" aria-hidden="true" data-testid="navbar-fade" />
        <AppNavbar :xl="xl" :lg="lg" @toggle-nav="ui.setNavOpen(!ui.navOpen)" />
      </div>
      <main class="layout-vertical__content" :class="route.meta.contentClass" data-app-content>
        <Transition name="layout-fade">
          <div v-show="!canvas" class="layout-vertical__page">
            <AppBreadcrumb />
            <div v-if="sidebarLeft" class="layout-vertical__sidebar-left" data-testid="content-sidebar-frame">
              <div :id="CONTENT_SIDEBAR_TARGET_ID" class="layout-vertical__sidebar-target" />
              <div class="layout-vertical__sidebar-page"><slot /></div>
            </div>
            <slot v-else />
          </div>
        </Transition>
        <div
          v-if="canvas"
          :id="ASSISTANT_CANVAS_TARGET_ID"
          class="layout-vertical__canvas"
          data-testid="assistant-canvas-target"
        />
      </main>
    </div>
  </div>
</template>

<style scoped>
.layout-vertical__main {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  transition:
    margin-left 0.3s ease,
    margin-right 0.3s ease;
}
.layout-vertical.is-xl.has-assistant .layout-vertical__main {
  margin-right: calc(var(--assistant-width, 360px) + var(--layout-gutter));
}
.layout-vertical.has-assistant-pill .layout-vertical__main {
  padding-bottom: 7rem;
}
.layout-vertical {
  /* The vertical nav floats: this gutter separates it from the viewport edges (the navbar's own top margin). */
  --layout-gutter: 1.3rem;
}
.layout-vertical.is-xl .layout-vertical__main {
  margin-left: calc(260px + var(--layout-gutter));
}
.layout-vertical.is-xl.is-collapsed .layout-vertical__main {
  margin-left: calc(80px + var(--layout-gutter));
}
/* The band behind the bar (`.edge-fade`, theme/tailwind.css) is a child, so the wrapper is its containing block;
   the wrapper's own stacking context keeps the band under the bar and over the page. */
.layout-vertical__navbar {
  position: sticky;
  top: 0;
  z-index: 12;
  padding: 1.3rem 1.2rem 0;
}
.layout-vertical__navbar > .edge-fade {
  /* Reach a little below the bar so the ramp ends past the controls, not at their baseline. */
  bottom: -1.25rem;
}
.layout-vertical.is-xl .layout-vertical__navbar {
  padding: 1.3rem 2rem 0;
}
.layout-vertical__content {
  flex: 1;
  padding: 2rem 1.2rem var(--layout-gutter);
}
.layout-vertical.is-xl .layout-vertical__content {
  padding: 2rem 2rem var(--layout-gutter);
}
/* Legacy `.content-area-wrapper` of the left-sidebar renderer (+ the inbox's border): one frame, the sidebar target and
   the page side by side; positioned, so the sidebar and its overlay slide over the page below 992 px. The height is
   the window less what lies above it (navbar, content padding, breadcrumb: 10.6 rem) and the gutter below it, so it
   ends level with the nav (legacy's 11.1 rem also held a footer, since dropped). */
.layout-vertical__sidebar-left {
  position: relative;
  display: flex;
  height: calc(100vh - 11.9rem);
  min-height: 25rem;
  overflow: hidden;
  background: var(--p-content-background);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
}
.layout-vertical__sidebar-target {
  display: flex;
  flex: 0 0 auto;
}
.layout-vertical__sidebar-page {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  overflow: auto;
}
/* The canvas lies over the content area and what it replaces fades away under it: the nav (its fixed panel fades
   with the wrapper) and the page (kept mounted, so it is as the user left it). */
.layout-fade-enter-active,
.layout-fade-leave-active {
  transition: opacity 0.3s ease;
}
.layout-fade-enter-from,
.layout-fade-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .layout-fade-enter-active,
  .layout-fade-leave-active {
    transition: none;
  }
}
/* The canvas stands in for the page: the main column is the window's height, nothing scrolls but the canvas, and the
   nav's room on the left goes to it. */
/* (Written out for the collapsed rail too: its own margin rule is more specific.) */
.layout-vertical.has-canvas .layout-vertical__main,
.layout-vertical.is-xl.is-collapsed.has-canvas .layout-vertical__main {
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  margin-left: 0;
}
.layout-vertical.has-canvas .layout-vertical__content {
  position: relative;
  min-height: 0;
  overflow: hidden;
}
/* Over the content area, inside its padding (the page fading out stays under it). */
.layout-vertical__canvas {
  position: absolute;
  inset: 2rem 1.2rem var(--layout-gutter);
  z-index: 2;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.layout-vertical.is-xl .layout-vertical__canvas {
  inset: 2rem 2rem var(--layout-gutter);
}
.layout-vertical__canvas > * {
  flex: 1 1 auto;
  min-height: 0;
}
.layout-vertical__overlay {
  position: fixed;
  inset: 0;
  z-index: 1030;
  background: rgb(34 41 47 / 50%);
}
</style>
